/**
 * React Query hooks for fetching notification data
 */

import { useApiClient } from '@commander/api-client';
import { useQuery } from '@tanstack/react-query';
import { createNotificationApi } from '../../api/notificationApi';
import type { NotificationApp, NotificationListParams } from '../../types';
import { notificationKeys } from '../notificationKeys';

/**
 * Hook to fetch paginated list of notifications
 * Uses React Query for automatic caching, refetching, and loading states
 */
export function useNotifications(params?: NotificationListParams, options?: { enabled?: boolean }) {
  const apiClient = useApiClient();
  const notificationApi = createNotificationApi(apiClient);

  return useQuery({
    queryKey: notificationKeys.list(params),
    queryFn: () => notificationApi.getNotifications(params),
    enabled: options?.enabled !== false,
  });
}

/**
 * Hook to fetch notifications for a specific app
 */
export function useAppNotifications(
  app: NotificationApp,
  params?: Omit<NotificationListParams, 'app'>,
  options?: { enabled?: boolean }
) {
  return useNotifications({ ...params, app }, options);
}

/**
 * Hook to fetch only unread notification count
 * Useful for badges and indicators
 */
export function useUnreadNotificationCount(app?: NotificationApp, options?: { enabled?: boolean }) {
  const apiClient = useApiClient();
  const notificationApi = createNotificationApi(apiClient);

  return useQuery({
    queryKey: notificationKeys.unreadCount(app),
    queryFn: () => notificationApi.getUnreadCount(app),
    enabled: options?.enabled !== false,
    retry: false, // Don't retry failed requests for notification count
  });
}

/**
 * Hook to fetch notification preferences
 */
export function useNotificationPreferences(options?: { enabled?: boolean }) {
  const apiClient = useApiClient();
  const notificationApi = createNotificationApi(apiClient);

  return useQuery({
    queryKey: notificationKeys.preferences(),
    queryFn: () => notificationApi.getPreferences(),
    enabled: options?.enabled !== false,
  });
}
